/** The query parameter that keeps the selected settings tab: `?tab=<key>`. */
export const TAB_PARAM = 'tab';

/**
 * The tab to show, as its key: the one `?tab=` names while it is a visible tab, else `fallback` while
 * that is a visible tab (SettingsTabs' `initialTab`, then the person's last choice), else the first
 * visible tab. Null only when no tab is visible. A hidden tab counts as unknown, so a link to a tab this
 * person cannot see opens the first one they can.
 */
export function resolveTab(
  tabs: readonly { key: string; visible?: boolean }[],
  urlKey: string | null,
  fallback: string | null,
): string | null {
  const visible = tabs.filter((tab) => tab.visible !== false);
  const isVisible = (key: string | null) => key !== null && visible.some((tab) => tab.key === key);
  if (isVisible(urlKey)) return urlKey;
  if (isVisible(fallback)) return fallback;
  return visible[0]?.key ?? null;
}

/**
 * The tab a key moves the focus to from `current`, or undefined for a key the tab list does not handle.
 * Down and Right go to the next tab, Up and Left to the previous one, both wrapping round; Home goes to
 * the first and End to the last. Right and Left as well as Down and Up because on a narrow screen the
 * list wraps into rows.
 */
export function tabAfterKey(keys: readonly string[], current: string, key: string): string | undefined {
  const at = keys.indexOf(current);
  if (at === -1 || keys.length === 0) return undefined;
  switch (key) {
    case 'ArrowDown':
    case 'ArrowRight':
      return keys[(at + 1) % keys.length];
    case 'ArrowUp':
    case 'ArrowLeft':
      return keys[(at - 1 + keys.length) % keys.length];
    case 'Home':
      return keys[0];
    case 'End':
      return keys[keys.length - 1];
    default:
      return undefined;
  }
}
